import { useState, useRef, useEffect } from 'react';

interface HoverPreviewProps {
  /** 미리보기할 이미지 URL */
  imageUrl: string;
  /** 트리거 요소 */
  children: React.ReactNode;
  /** 미리보기 크기 */
  size?: 'sm' | 'md' | 'lg';
  /** 표시 위치 (currently only auto is implemented) */
  _position?: 'auto' | 'left' | 'right' | 'top' | 'bottom';
  /** 표시 지연 시간 (ms) */
  delay?: number;
}

const sizeClasses = {
  sm: 'w-48 h-48',
  md: 'w-64 h-64',
  lg: 'w-80 h-80',
};

export function HoverPreview({
  imageUrl,
  children,
  size = 'md',
  delay = 300,
}: HoverPreviewProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    // 자동 위치 계산
    let x = rect.right + 10;
    let y = rect.top;

    // 화면 오른쪽 경계 체크
    if (x + 256 > window.innerWidth) {
      x = rect.left - 256 - 10;
    }

    // 화면 하단 경계 체크
    if (y + 256 > window.innerHeight) {
      y = window.innerHeight - 256 - 10;
    }

    setCoords({ x, y });

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>

      {/* 미리보기 팝업 */}
      {isVisible && (
        <div
          className={`
            fixed z-50 bg-white rounded-card shadow-2xl overflow-hidden border
            ${sizeClasses[size]}
            animate-fade-in
          `}
          style={{
            left: coords.x,
            top: coords.y,
          }}
        >
          <img
            src={imageUrl}
            alt="미리보기"
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </>
  );
}

/**
 * 호버 시 카드 정보 미리보기
 */
interface CardHoverPreviewProps {
  /** 제목 */
  title: string;
  /** 이미지 URL */
  imageUrl?: string;
  /** 추가 정보 */
  details?: Array<{ label: string; value: string }>;
  /** 트리거 요소 */
  children: React.ReactNode;
  /** 표시 지연 시간 */
  delay?: number;
}

export function CardHoverPreview({
  title,
  imageUrl,
  details = [],
  children,
  delay = 400,
}: CardHoverPreviewProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    let x = rect.right + 10;
    let y = rect.top;

    if (x + 300 > window.innerWidth) {
      x = rect.left - 300 - 10;
    }

    if (y + 200 > window.innerHeight) {
      y = window.innerHeight - 200 - 10;
    }

    setCoords({ x, y });

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>

      {isVisible && (
        <div
          className="fixed z-50 w-72 bg-white rounded-card shadow-2xl overflow-hidden border animate-fade-in"
          style={{
            left: coords.x,
            top: coords.y,
          }}
        >
          {/* 이미지 */}
          {imageUrl && (
            <div className="w-full h-40 bg-ink-100">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* 정보 */}
          <div className="p-3">
            <h4 className="font-medium text-ink-900 mb-2">{title}</h4>
            {details.length > 0 && (
              <dl className="space-y-1 text-sm">
                {details.map((detail, i) => (
                  <div key={i} className="flex justify-between">
                    <dt className="text-ink-500">{detail.label}</dt>
                    <dd className="text-ink-800 font-medium">{detail.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default HoverPreview;
